import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Outlet, Route, Routes, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from '../../i18n/useTranslation';
import { setNavigate } from './navigation.tsx';
import PublicRoute from './public_route.tsx';
import PrivateRoute from './private_route.tsx';

// Lazy loading de toutes les pages pour améliorer FCP/LCP
const Welcome = lazy(() => import('../../features/authentication/presentation/pages/welcome.tsx'));
const LoginPage = lazy(() => import('../../features/authentication/presentation/pages/login.tsx'));
const RegisterPage = lazy(() => import('../../features/authentication/presentation/pages/register.tsx'));
const HomePage = lazy(() => import('../../features/home/presentation/pages/home.tsx'));
const PrepareVibePage = lazy(() => import('../../features/vibes/presentation/pages/prepare_vibe_page.tsx'));
const VibePage = lazy(() => import('../../features/vibes/presentation/pages/vibe_page.tsx'));
const ForgotPassword = lazy(() => import('../../features/authentication/presentation/pages/forgot_password.tsx'));
const EmailSent = lazy(() => import('../../features/authentication/presentation/pages/email_sent.tsx'));
const ResetPassword = lazy(() => import('../../features/authentication/presentation/pages/reset_password.tsx'));
const PasswordReset = lazy(() => import('../../features/authentication/presentation/pages/password_reset.tsx'));
const RewardsPage = lazy(() => import('../../features/rewards/presentation/pages/rewards_page.tsx'));
const EndVibePage = lazy(() => import('../../features/vibes/presentation/pages/end_vibe_page.tsx'));
const ProfilePage = lazy(() => import('../../features/profile/presentation/pages/profile_page.tsx'));
import ExceptionalRewardsPage from '../../features/rewards/presentation/pages/exclusive_rewards_page.tsx';
import SurveyPage from '../../features/survey/presentation/pages/survey_page.tsx';
import AnswerSurveyPage from '../../features/survey/presentation/pages/answer_survey_page.tsx';
import SurveyAnsweredPage from '../../features/survey/presentation/pages/survey_answered_page.tsx';
import NewsPage from '../../features/news/presentation/pages/news_page.tsx';
import ReadNewsPage from '../../features/news/presentation/pages/read_news_page.tsx';
import EditProfilePage from '../../features/profile/presentation/pages/edit_profile_page.tsx';
import ChangePasswordPage from '../../features/profile/presentation/pages/change_password_page.tsx';
import EthicalCharter from '../../core/components/ethical_charter.tsx';
import UvibesHelp from '../../core/components/uvibes_help.tsx';
import CGUUvibes from '../../core/components/cgu_uvibes.tsx';
import PCUvibes from '../../core/components/pc_uvibes.tsx';
import ReportVibesPage from '../../features/vibes/presentation/pages/report_vibes_page.tsx';
import { VibeWebsocketProvider } from '../../features/vibes/domain/providers/vibe_websocket_provider.tsx';
import SessionCalendarPage from '../../features/home/presentation/pages/session_calendar_page.tsx';
import LoadingPage from '../../core/components/user/presentation/loading_page.tsx';
import StartPage from '../../features/course/presentation/pages/start_page.tsx';
import ThemeDetailPage from '../../features/course/presentation/pages/theme_detail_page.tsx';
import StartQuizzPage from '../../features/course/presentation/pages/start_quizz_page.tsx';
import EndQuizzPage from '../../features/course/presentation/pages/end_quizz_page.tsx';
import ThemePage from '../../features/course/presentation/pages/theme_page.tsx';
import ResourceTypePage from '../../features/course/presentation/pages/type_resource_page.tsx';
import ResourcePage from '../../features/course/presentation/pages/resource_page.tsx';
import CertificatePage from '../../features/course/presentation/pages/certificate_page.tsx';
import { UserInterface } from '../../core/components/user/domain/interfaces/user_interface.tsx';
import { getCurrentUserTheme } from '../../features/course/domain/utils/get_current_user_theme.tsx';
import UserCertificatesPage from '../../features/profile/presentation/pages/user_certificates_page.tsx';


// Composant pour configurer la navigation
const NavigationSetter: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
};

// Composant pour redirection « catch-all »
const Redirect: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/');
  }, [navigate]);
  return null;
};

// Composant pour la redirection des vibes
const VibeRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const redirectPath = `${location.pathname}/prepare/access`;
    navigate(redirectPath, { state: { from: location }, replace: true });
  }, [location, navigate]);
  
  return null;
};

const VibeWebsocketLayout: React.FC = () => {
  const ctx = useOutletContext<UserInterface>();
  return (
    <VibeWebsocketProvider>
      <Outlet context={ctx}/>
    </VibeWebsocketProvider>
  );
};

const CareerGuard: React.FC = () => {
  const { t } = useTranslation();
  const ctx = useOutletContext<UserInterface>();
  const navigate = useNavigate();
  const { company } = ctx;
  const blocked = company?.permissions.ressource === false && company?.permissions.course === false;

  useEffect(() => {
    if (blocked) {
      toast.error(t('course.access_denied'));
      navigate('/', { replace: true });
    }
  }, [blocked, navigate, t]);

  if (blocked) return null;
  return <Outlet context={ctx}/>;
};

const CareerRedirect: React.FC = () => {
  const { course_theme, company } = useOutletContext<UserInterface>();
  const navigate = useNavigate();

  useEffect(() => {
    const currentThemeInProgress = getCurrentUserTheme(course_theme, company?.id);
    if (currentThemeInProgress) {
      const themeId = typeof currentThemeInProgress._themeId === 'string' ? currentThemeInProgress._themeId : currentThemeInProgress._themeId!._id;
      navigate(`/career/${themeId}/resources`, { replace: true });
    } else {
      navigate('/career/start', { replace: true });
    }
  }, [course_theme, company?.id, navigate]);

  return null;
}

function AppRouter() {
    const { t } = useTranslation();

    const PublicRoutes = () => {
        return (
            <Route path='/' element={<PublicRoute homeLocation='/'/>}>
                <Route path='/welcome' element={<Welcome/>}/>
                <Route path='/login' element={<LoginPage/>}/>
                <Route path='/register/:step?' element={<RegisterPage/>}/>
                <Route path='/forgot-password' element={<ForgotPassword/>}/>
                <Route path='/email-sent' element={<EmailSent/>}/>
                <Route path='/reset-password' element={<ResetPassword/>}/>
                <Route path='/password-reset' element={<PasswordReset/>}/>
            </Route>
        )
    }

    const PrivateRoutes = () => {
        return (
            <Route path='/' element={<PrivateRoute loginLocation='/welcome'/>}>
                <Route index element={<HomePage/>}/>
                <Route path='/vibes/:sessionId' element={<VibeRedirect/>}/>
                <Route element={<VibeWebsocketLayout/>}>
                  <Route path='/vibes/:sessionId/prepare/:step?' element={<PrepareVibePage/>}/>
                  <Route path='/vibes/:sessionId/:meetingId' element={
                    <Suspense fallback={
                      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-500 mx-auto mb-4"></div>
                          <p className="text-gray-600">{t('vibes.loading')}</p>
                        </div>
                      </div>
                    }>
                      <VibePage/>
                    </Suspense>
                  }/>
                  <Route path='/vibes/:sessionId/:meetingId/end/:step?' element={<EndVibePage/>}/>
                  <Route path='/vibes/:sessionId/:meetingId/report/:step?' element={<ReportVibesPage/>}/>
                </Route>
                <Route path='/rewards' element={<RewardsPage/>}/>
                <Route path='/rewards/exceptional' element={<ExceptionalRewardsPage/>}/>
                <Route path='/profile/me' element={<ProfilePage/>}/>
                <Route path='/profile/me/report/:meetingId/:step?' element={<ReportVibesPage/>}/>
                <Route path='/profile/me/certificates' element={<UserCertificatesPage/>}/>
                <Route path='/profile/edit' element={<EditProfilePage />}/>
                <Route path='/profile/change-password' element={<ChangePasswordPage />}/>
                <Route path='/survey' element={<SurveyPage/>}/>
                <Route path='/survey/:surveyId/:questionId?' element={<AnswerSurveyPage/>}/>
                <Route path='/survey/:surveyId/end' element={<SurveyAnsweredPage/>}/>
                <Route path='/news' element={<NewsPage/>}/>
                <Route path='/news/:newsId' element={<ReadNewsPage/>}/>
                <Route path='/vibes' element={<SessionCalendarPage/>}/>
                <Route path="/career/:themeId/certificate" element={<CertificatePage />} />
                <Route path="/career" element={<CareerGuard />}>
                  <Route index element={<CareerRedirect />} />
                  <Route path="start" element={<StartPage />} />
                  <Route path=":themeId" element={<ThemeDetailPage />} />
                  <Route path=":themeId/resources" element={<ThemePage />} />
                  <Route path="resource/:resourceId" element={<ResourcePage />} />
                  <Route path=":themeId/resources/:type" element={<ResourceTypePage />} />
                  <Route path=":themeId/quizz" element={<StartQuizzPage />} />
                  <Route path=":themeId/quizz/:questionId" element={<StartQuizzPage />} />
                  <Route path=":themeId/end-quizz" element={<EndQuizzPage />} />
                </Route>
            </Route>
        );
    }

    const NormalRoutes = () => {
      return (
        <>
          <Route path='/cgu' element={<CGUUvibes/>}/>
          <Route path='/pc' element={<PCUvibes/>}/>
          <Route path='/help' element={<UvibesHelp/>}/>
          <Route path='/ethical-charter' element={<EthicalCharter/>}/>
        </>
      );
    }

  return (
    <BrowserRouter>
      <NavigationSetter />
      <Suspense fallback={<LoadingPage error={false} isLoading={true} />}>
        <Routes>
          {PublicRoutes()}
          {PrivateRoutes()}
          {NormalRoutes()}
          <Route path="*" element={<Redirect />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default AppRouter;
